// strv-standard: dom v1.0.0
// Master copy lives in strvcom/strv-it-hub /standard. Copy unchanged.
//
// A tiny element builder. Text is always set as text (never as HTML), so
// values from the database can't inject markup.

/**
 * el('button', { class: 'btn', onclick: fn, 'aria-label': 'Close' }, 'Close')
 * - `class` accepts a string or an array (falsy entries dropped)
 * - `on<event>` keys become listeners; `dataset` takes an object
 * - `html` sets trusted markup (icons only — never user data)
 * - children: strings, numbers, Nodes, arrays, null/false (skipped)
 * @param {string} tag
 * @param {Record<string, any>} [attrs]
 * @param {...any} children
 * @returns {HTMLElement}
 */
export function el(tag, attrs = {}, ...children) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs || {})) {
    if (value === undefined || value === null || value === false) continue;
    if (key === 'class') {
      node.className = Array.isArray(value) ? value.filter(Boolean).join(' ') : value;
    } else if (key === 'dataset') {
      Object.assign(node.dataset, value);
    } else if (key === 'html') {
      node.innerHTML = value;
    } else if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (value === true) {
      node.setAttribute(key, '');
    } else {
      node.setAttribute(key, String(value));
    }
  }
  append(node, children);
  return node;
}

/** Append children (same rules as el). */
export function append(node, children) {
  for (const child of children.flat(Infinity)) {
    if (child === null || child === undefined || child === false) continue;
    node.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return node;
}

/** Remove all children. */
export function clear(node) {
  while (node.firstChild) node.firstChild.remove();
  return node;
}

/** Initials for an avatar: "Daniel Kysilka" → "DK". */
export function initials(name = '', email = '') {
  const source = name.trim() || email.split('@')[0].replace(/[._-]+/g, ' ');
  const parts = source.split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts.at(-1)[0] : (parts[0] || '?').slice(0, 2);
  return letters.toUpperCase();
}

/** Square avatar with initials fallback when the photo is missing or fails. */
export function avatar({ name = '', email = '', photoURL = '' } = {}, size = '') {
  const box = el('span', { class: ['avatar', size], 'aria-hidden': 'true' }, initials(name, email));
  if (photoURL) {
    const img = el('img', { src: photoURL, alt: '', referrerpolicy: 'no-referrer' });
    img.addEventListener('load', () => {
      clear(box);
      box.append(img);
    });
  }
  return box;
}
